import React, { useEffect } from "react";
import { Form, Radio, notification, Modal } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import httpClient from "../../api/module/httpClient";
import { CustomButton, CustomInput, CustomRadio } from "../../components";

/**
 *
 * do not remove values, just add them as this is the source of truth for the questions
 * if should we retire a question, we just add an indicator that the question is not being used anymore
 * so we can keep track of the question using the id in the backend
 */
const questions = [
  {
    id: 1,
    question: "Which aspect of today’s class did you find most valuable for your professional growth and development?",
    type: "text",
  },
  {
    id: 2,
    question: "How confident do you feel in applying the concepts and skills from today's class to your current role? (Rate from 1-5, with 5 being very confident)",
    type: "radio",
  },
  {
    id: 3,
    question: "How would you rate the overall quality and delivery of this class? (Rate from 1-5, with 5 being excellent)",
    type: "radio",
  },
  {
    id: 4,
    question: "How would you evaluate the faculty member’s effectiveness in delivering today’s content and engaging participants? (Rate from 1-5, with 5 being excellent)",
    type: "radio",
  },
  {
    id: 5,
    question: "How would you evaluate the facilitator’s ability to engage participants, follow the agenda, and create a productive learning environment? (Rate from 1-5, with 5 being excellent)",
    type: "radio",
  },
  {
    id: 6,
    question: "Was the pace of the class appropriate for covering the material effectively? (Rate from 1-5, with 5 being just right)",
    type: "radio",
  },
  {
    id: 7,
    question: "Do you have any additional suggestions for improving this class or enhancing future sessions?",
    type: "text",
  },
];

export default function FeedbackReviewModal({ open, classId, certificateId, onOpen, onClose }) {
  const [form] = Form.useForm();

  const queryClient = useQueryClient();

  const submitReviewMutation = useMutation({
    mutationFn: async ({ review, id }) => {
      return httpClient.post(`private/certificate-class/feedback-review`, { review, id, classId, certificateId });
    },
    onSuccess: () => {
      notification.success({
        message: "Success",
      });

      queryClient.invalidateQueries(["FEEDBACK_REVIEW", classId, certificateId]);
      form.resetFields();
      onClose();
    },
    onError: () => {
      notification.error({
        message: "Something went wrong",
      });
      form.resetFields();
    },
  });

  const { data } = useQuery({
    queryKey: ["FEEDBACK_REVIEW", classId, certificateId],
    queryFn: async () => {
      const { data } = await httpClient.get(`private/course/${certificateId}/certificate-class/${classId}/feedback-review`);

      return data;
    },
    enabled: !!classId && !!certificateId,
  });

  useEffect(() => {
    form.resetFields();
  }, [form, data]);


  function handleSubmit(values) {
    const { id, ...rest } = values;
    const ids = Object.keys(rest);

    ids.forEach(id => {
      const questionItem = questions.find((question) => question.id === +id);

      rest[id] = {
        answer: rest[id],
        ...questionItem,
      };

    });

    submitReviewMutation.mutate({ review: Object.values(rest), id });
  }

  const initialValues = (data?.review ?? []).map(data => ({
    id: data?.id || null,
    [data.id]: data.answer,
  })).reduce((acc, curr) => {
    const key = Object.keys(curr)[0];
    acc[key] = curr[key];
    return acc;
  }, {});


  return (
    <Modal
      open={open}
      onCancel={() => {
        onClose();
      }}
      onOk={() => {
        onOpen();
      }}
      width={800}
      destroyOnClose={true}
      forceRender={true}
      cancelButtonProps={{ style: { display: 'none' } }}
      okButtonProps={{ style: { display: 'none' } }}
    >
      <div>
        <div style={{ width: "90%", margin: "auto", marginTop: "1rem" }}>
          <Form form={form} onFinish={handleSubmit} layout="vertical" initialValues={initialValues}>
            <Form.Item hidden={true} name="id" initialValue={data?.id || null} />
            <h2>Feedback Review</h2>
            <div style={{ display: "flex", flexDirection: "column", gap: "2rem", marginTop: "1rem" }}>
              {questions.map(({ question, id, type }) => {
                return (
                  <>
                    {type === "radio" && (
                      <Form.Item
                        name={id}
                        key={question}
                        label={question}
                        rules={[{ required: true, message: "Input is required." }]}
                      >
                        <Radio.Group style={{ display: "flex", marginTop: "1rem" }}>
                          {[1, 2, 3, 4, 5].map(answer => <CustomRadio value={answer}>{answer}</CustomRadio>)}
                        </Radio.Group>
                      </Form.Item>
                    )}
                    {type === "text" && (
                      <Form.Item
                        name={id}
                        key={question}
                        label={question}
                        rules={[{ required: type === "radio", message: "Input is required." }]}
                      >
                        <CustomInput multiple={true} />
                      </Form.Item>
                    )}
                  </>
                );
              })}
            </div>
            <Form.Item>
              <CustomButton text="Submit" htmlType="submit" />
            </Form.Item>
          </Form>
        </div>
      </div>
    </Modal>
  );
}